import { OptionType } from "@qendo/types";
import { useEffect, useMemo, useState } from "react";
import { View } from "react-native";
import { Chip } from "react-native-paper";
import { OptionSelector } from "~/components/form/combo-box/OptionSelector";
import type { PassthroughFieldLabelProps } from "~/components/form/FieldLabel";
import { Subtitle } from "~/components/layout/text";
import { QendoIcon } from "~/components/QendoIcon";
import { Flex, Gap, Items } from "~/constants/layout";
import { useOptions } from "~/providers/OptionsProvider";
import { useTheme } from "~/providers/ThemeProvider";
import { filled } from "~/util";
import { filterUnique } from "~/util/array";

export type TreatmentSelectorProps = {
    name?: string;
    value: string[];
    onChangeSelection: (value: string[]) => void;
    disabled?: boolean;
} & PassthroughFieldLabelProps;

export const TreatmentSelector = ({
    value,
    onChangeSelection,
    disabled,
    label = "Treatments",
    ...labelProps
}: TreatmentSelectorProps) => {
    const { findOption } = useOptions();
    const { colours } = useTheme();
    const [treatment, setTreatment] = useState<string | null>(null);

    const selected = useMemo(
        () => value.map((id) => findOption(OptionType.symptoms_treatments, id)).filter(filled),
        [value, findOption],
    );

    const onRemove = (id: string) => {
        onChangeSelection(value.filter((v) => v !== id));
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies(onChangeSelection): setter
    // biome-ignore lint/correctness/useExhaustiveDependencies(value): read only
    useEffect(() => {
        if (treatment) {
            setTreatment(null);
            onChangeSelection([...value, treatment].filter(filterUnique));
        }
    }, [treatment]);

    return (
        <View style={Gap.sm}>
            <OptionSelector
                {...labelProps}
                type={OptionType.symptoms_treatments}
                value={treatment}
                onItemSelected={setTreatment}
                label={label}
                exclude={value}
                disabled={disabled}
                placeholder="Find or add treatment"
            />
            {filled(selected) && (
                <View style={[Flex.wrap, Flex.row, Gap.sm]}>
                    {selected.map((t) => (
                        <Chip
                            key={t.id}
                            mode="outlined"
                            selected
                            showSelectedOverlay
                            showSelectedCheck={false}
                            selectedColor={colours.text.selected}
                            textStyle={{ color: colours.text.secondary }}
                            style={[Items.center, { backgroundColor: colours.button["secondary-background"] }]}
                            onPress={() => onRemove(t.id)}
                            disabled={disabled}
                        >
                            <View style={[Flex.row, Gap.xs, Items.center]}>
                                <Subtitle size="sm">{t.title}</Subtitle>
                                <QendoIcon name="xmark" size={16} />
                            </View>
                        </Chip>
                    ))}
                </View>
            )}
        </View>
    );
};
